import { useState, useCallback } from "react";

/**
 * Custom hook for managing highlight state and operations
 * @param {React.RefObject} videoRef - Reference to the video element
 * @returns {Object} Highlight state and handler functions
 */
const useHighlights = (videoRef) => {
  const [highlights, setHighlights] = useState([]);
  const [selectedHighlight, setSelectedHighlight] = useState(null);
  const [activeHighlight, setActiveHighlight] = useState(null);

  const handleAddHighlight = useCallback(() => {
    if (!videoRef.current) return;

    const currentTime = videoRef.current.currentTime;
    const videoDuration = videoRef.current.duration;

    // Calculate start time (3 seconds before current time)
    let startTime = Math.max(0, currentTime - 3);

    const newHighlight = {
      id: Date.now(),
      startTime: startTime,
      duration: 6,
      slowMotion: false,
      text: '',
      color: '#ffffff',
    };

    // If the highlight would extend past the video end, adjust the duration
    if (startTime + 6 > videoDuration) {
      newHighlight.duration = videoDuration - startTime;
    }

    setHighlights(prev => [...prev, newHighlight]);
    setSelectedHighlight(newHighlight);
  }, [videoRef]);

  const handleSelectHighlight = useCallback((highlight) => {
    setSelectedHighlight(highlight);
  }, []);

  const handleUpdateHighlight = useCallback((field, value) => {
    setSelectedHighlight(currentSelected => {
      if (!currentSelected) return null;

      const updatedHighlight = { ...currentSelected, [field]: value };

      setHighlights(prev =>
        prev.map(h => h.id === currentSelected.id ? updatedHighlight : h)
      );

      setActiveHighlight(currentActive => {
        if (currentActive && currentActive.id === updatedHighlight.id) {
          if (field === 'slowMotion' && videoRef.current) {
            videoRef.current.playbackRate = value ? 0.5 : 1;
          }
          return updatedHighlight;
        }
        return currentActive;
      });

      return updatedHighlight;
    });
  }, [videoRef]);

  const handleDeleteHighlight = useCallback((id) => {
    setHighlights(prev => prev.filter(h => h.id !== id));
    setSelectedHighlight(current => current?.id === id ? null : current);
    setActiveHighlight(current => current?.id === id ? null : current);
  }, []);

  // Source timeline click: select only, no activate/loop
  const handleHighlightClick = useCallback((highlight) => {
    setSelectedHighlight(highlight);
  }, []);

  // Output timeline click: select + activate (green/loop)
  const handleOutputHighlightClick = useCallback((highlight) => {
    setSelectedHighlight(highlight);

    setActiveHighlight(currentActive => {
      if (currentActive && currentActive.id === highlight.id) {
        if (videoRef.current) {
          videoRef.current.pause();
          videoRef.current.playbackRate = 1;
        }
        return null;
      } else {
        if (videoRef.current) {
          videoRef.current.currentTime = highlight.startTime;
          videoRef.current.playbackRate = highlight.slowMotion ? 0.5 : 1;
          videoRef.current.play();
        }
        return highlight;
      }
    });
  }, [videoRef]);

  const handleMouseDown = useCallback((highlight, e) => {
    e.preventDefault();
    setSelectedHighlight(highlight);
  }, []);

  const handleDuplicateHighlight = useCallback(() => {
    setSelectedHighlight(current => {
      if (!current) return null;

      const duplicate = {
        ...current,
        id: Date.now(),
      };

      setHighlights(prev => [...prev, duplicate]);
      return duplicate;
    });
  }, []);

  // Reset function for clearing all highlight state
  const resetHighlights = useCallback(() => {
    setHighlights([]);
    setSelectedHighlight(null);
    setActiveHighlight(null);
  }, []);

  return {
    highlights,
    setHighlights,
    selectedHighlight,
    setSelectedHighlight,
    activeHighlight,
    setActiveHighlight,
    handleAddHighlight,
    handleSelectHighlight,
    handleUpdateHighlight,
    handleDeleteHighlight,
    handleHighlightClick,
    handleOutputHighlightClick,
    handleMouseDown,
    handleDuplicateHighlight,
    resetHighlights,
  };
};

export default useHighlights;
